import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, BookOpen, ChefHat, Compass, Refrigerator } from 'lucide-react';
import { useCookingStore } from '@/stores/cooking-store';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
}

function isRouteActive(pathname: string, targetPath: string): boolean {
  if (targetPath === '/') return pathname === '/';
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
}

export function MobileNav() {
  const location = useLocation();
  const { t } = useTranslation('common');
  const isSessionActive = useCookingStore((state) => state.isSessionActive);

  const navItems: NavItem[] = [
    { to: '/', label: t('nav.home'), icon: Home },
    { to: '/discover', label: t('nav.discover'), icon: Compass },
    { to: '/cook', label: t('nav.cook'), icon: ChefHat },
    { to: '/pantry', label: t('nav.pantry'), icon: Refrigerator },
    { to: '/cookbook', label: t('nav.cookbook'), icon: BookOpen },
  ];

  if (location.pathname.startsWith('/cook/session')) {
    return null;
  }

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        pointerEvents: 'none',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      className="lg:hidden"
      role="navigation"
      aria-label={t('a11y.mainNav')}
    >
      {/* Floating capsule */}
      <div
        style={{
          pointerEvents: 'auto',
          margin: '0 12px 12px 12px',
          background: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(20px) saturate(1.8)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
          borderRadius: '22px',
          border: '1px solid rgba(255, 255, 255, 0.45)',
          boxShadow: 'var(--shadow-capsule)',
        }}
      >
        <ul style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          height: '60px',
          margin: 0,
          padding: '0 4px',
          listStyle: 'none',
        }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isRouteActive(location.pathname, item.to);
            const isCook = item.to === '/cook';
            const showIndicator = isCook && isSessionActive;

            if (isCook) {
              return (
                <li key={item.to} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <NavLink
                    to={item.to}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '3px',
                      textDecoration: 'none',
                      padding: '4px 6px',
                    }}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span
                      style={{
                        position: 'relative',
                        width: '44px',
                        height: '32px',
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-hover) 100%)',
                        boxShadow: isActive
                          ? '0 3px 12px rgba(8, 145, 178, 0.5), 0 0 0 2.5px rgba(6, 182, 212, 0.25)'
                          : '0 2px 8px rgba(8, 145, 178, 0.3)',
                        transition: 'all var(--motion-duration-standard) cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                      }}
                    >
                      <Icon style={{
                        width: '19px',
                        height: '19px',
                        color: 'white',
                        transition: 'color var(--motion-duration-fast) var(--motion-ease-standard)',
                      }} />
                      {showIndicator && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '-2px',
                            insetInlineEnd: '-2px',
                            width: '9px',
                            height: '9px',
                            borderRadius: '50%',
                            background: 'var(--accent-success)',
                            border: '2px solid white',
                            animation: 'pulse 1.8s ease-in-out infinite',
                          }}
                        />
                      )}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: isActive ? 700 : 600,
                      color: isActive ? 'var(--accent-primary-hover)' : 'var(--text-tertiary)',
                      transition: 'color var(--motion-duration-fast) var(--motion-ease-standard)',
                    }}>
                      {item.label}
                    </span>
                  </NavLink>
                </li>
              );
            }

            return (
              <li key={item.to} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <NavLink
                  to={item.to}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    padding: '4px 6px',
                    textDecoration: 'none',
                  }}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span
                    style={{
                      width: '44px',
                      height: '32px',
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isActive ? 'rgba(8, 145, 178, 0.1)' : 'transparent',
                      transition: 'background var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-standard) cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transform: isActive ? 'scale(1.05)' : 'scale(1)',
                    }}
                  >
                    <Icon style={{
                      width: '19px',
                      height: '19px',
                      color: isActive ? 'var(--accent-primary-hover)' : 'var(--text-muted)',
                      transition: 'color var(--motion-duration-fast) var(--motion-ease-standard)',
                    }} />
                  </span>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? 'var(--accent-primary-hover)' : 'var(--text-muted)',
                    transition: 'color var(--motion-duration-fast) var(--motion-ease-standard)',
                  }}>
                    {item.label}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
